import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { EventBusPort, PlatformEventMap, RuntimeEventMap } from '@ochal/module-sdk/contracts';
import {
  SessionExpiredError,
  type AuthAdapter,
  type AuthSession,
  type LoginCredentials,
} from './types';
import { createSessionSyncChannel, type SessionSyncChannel } from './session-sync';

export const DEFAULT_SESSION_IDLE_TIMEOUT_MS = 30 * 60 * 1_000;

interface AuthContextValue {
  session: AuthSession | null;
  loading: boolean;
  isAuthenticated: boolean;
  sessionExpired: boolean;
  sessionRemainingMs: number | null;
  login(credentials: LoginCredentials): Promise<void>;
  logout(): Promise<void>;
  clearSessionExpired(): void;
}
const AuthContext = createContext<AuthContextValue | null>(null);
export function AuthProvider({
  adapter,
  events,
  children,
  idleTimeoutMs = DEFAULT_SESSION_IDLE_TIMEOUT_MS,
}: {
  adapter: AuthAdapter;
  events?: EventBusPort<PlatformEventMap & RuntimeEventMap>;
  children: ReactNode;
  idleTimeoutMs?: number;
}) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionRemainingMs, setSessionRemainingMs] = useState<number | null>(null);
  const syncChannel = useRef<SessionSyncChannel | null>(null);
  useEffect(() => {
    void adapter
      .getSession()
      .then(setSession)
      .catch((error: unknown) => {
        if (error instanceof SessionExpiredError) setSessionExpired(true);
      })
      .finally(() => setLoading(false));
  }, [adapter]);
  const login = useCallback(
    async (credentials: LoginCredentials) => {
      const next = await adapter.login(credentials);
      setSession(next);
      setSessionExpired(false);
      await events?.emit('auth.login', { userId: next.user.id });
    },
    [adapter, events],
  );
  const logout = useCallback(async () => {
    const userId = session?.user.id;
    try {
      await adapter.logout();
    } catch {
      /* Local logout must always complete. */
    } finally {
      if (userId) syncChannel.current?.publish({ type: 'logout', userId, occurredAt: Date.now() });
      setSession(null);
      setSessionExpired(false);
    }
    await events?.emit('auth.logout', { userId });
  }, [adapter, events, session]);
  useEffect(() => {
    const effectiveIdleTimeout = session?.policy?.idleTimeoutMs ?? idleTimeoutMs;
    if (!session || effectiveIdleTimeout <= 0) return;
    let timeout: ReturnType<typeof setTimeout>;
    let lastActivityAt = Date.now();
    let lastBroadcastAt = 0;
    let lastServerTouchAt = Date.now();
    let expiring = false;
    const expire = async () => {
      if (expiring) return;
      expiring = true;
      const userId = session.user.id;
      try {
        await adapter.logout();
      } catch {
        /* Local expiry must not depend on network availability. */
      }
      setSession(null);
      setSessionExpired(true);
      syncChannel.current?.publish({ type: 'expired', userId, occurredAt: Date.now() });
      await events?.emit('auth.logout', { userId });
    };
    const schedule = () => {
      clearTimeout(timeout);
      const remaining = Math.min(
        effectiveIdleTimeout - (Date.now() - lastActivityAt),
        (session.policy?.absoluteExpiresAt ?? Number.POSITIVE_INFINITY) - Date.now(),
      );
      setSessionRemainingMs(
        Math.max(0, Number.isFinite(remaining) ? remaining : effectiveIdleTimeout),
      );
      if (remaining <= 0) {
        void expire();
        return;
      }
      timeout = setTimeout(() => void expire(), remaining);
    };
    const activity = (remoteOccurredAt?: number) => {
      if (
        Date.now() - lastActivityAt >= effectiveIdleTimeout ||
        (session.policy?.absoluteExpiresAt ?? Number.POSITIVE_INFINITY) <= Date.now()
      ) {
        void expire();
        return;
      }
      const now = Date.now();
      lastActivityAt = Math.max(lastActivityAt, remoteOccurredAt ?? now);
      if (remoteOccurredAt === undefined && now - lastBroadcastAt >= 1_000) {
        lastBroadcastAt = now;
        syncChannel.current?.publish({ type: 'activity', userId: session.user.id, occurredAt: now });
      }
      if (remoteOccurredAt === undefined && adapter.touchSession && now - lastServerTouchAt >= 60_000) {
        lastServerTouchAt = now;
        void adapter.touchSession().catch((error: unknown) => {
          if (error instanceof SessionExpiredError) void expire();
        });
      }
      schedule();
    };
    const channel = createSessionSyncChannel((message) => {
      if (message.userId !== session.user.id) return;
      if (message.type === 'activity') activity(message.occurredAt);
      if (message.type === 'logout' || message.type === 'expired') {
        setSession(null);
        setSessionExpired(message.type === 'expired');
      }
    });
    syncChannel.current = channel;
    const eventNames = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const;
    const onLocalActivity = () => activity();
    eventNames.forEach((eventName) =>
      window.addEventListener(eventName, onLocalActivity, { passive: true }),
    );
    document.addEventListener('visibilitychange', onLocalActivity);
    schedule();
    const ticker = setInterval(schedule, 30_000);
    return () => {
      clearTimeout(timeout);
      clearInterval(ticker);
      eventNames.forEach((eventName) => window.removeEventListener(eventName, onLocalActivity));
      document.removeEventListener('visibilitychange', onLocalActivity);
      setSessionRemainingMs(null);
      channel.close();
      if (syncChannel.current === channel) syncChannel.current = null;
    };
  }, [adapter, events, idleTimeoutMs, session]);
  const clearSessionExpired = useCallback(() => setSessionExpired(false), []);
  const value = useMemo(
    () => ({
      session,
      loading,
      isAuthenticated: Boolean(session),
      sessionExpired,
      sessionRemainingMs,
      login,
      logout,
      clearSessionExpired,
    }),
    [session, loading, sessionExpired, sessionRemainingMs, login, logout, clearSessionExpired],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
